import Icon from '@/components/icons/Icon';
import type { ReactHookFormRegister } from '@/components/modules/checkout/CheckoutFormFields/CheckoutFormFields';
import { classnames } from '@/helpers';
import { InputHTMLAttributes, LegacyRef, forwardRef, useId, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

type GenericListInputProps = {
  id: string;
  label?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  leftPadding?: boolean;
  rightPadding?: boolean;
  floatStyle?: boolean;
  setValue?: UseFormSetValue<any>;
  inputValue?: string;
};

type ListInputProps = InputHTMLAttributes<HTMLInputElement> & GenericListInputProps & ReactHookFormRegister<any>;

const ListInput = forwardRef(
  (
    {
      id,
      label,
      error,
      leftIcon,
      rightIcon,
      type = 'text',
      className,
      disabled,
      leftPadding = true,
      rightPadding = true,
      floatStyle = false,
      inputValue = '',
      setValue,
      onBlur: onBlurFromProps,
      onFocus: onFocusFromProps,
      ...props
    }: ListInputProps,
    ref: LegacyRef<HTMLInputElement>,
  ) => {
    // Minted per mount rather than taken from `id`: callers reuse field ids across
    // surfaces — a login modal's e-mail over the checkout's own — and `htmlFor`
    // resolves to whichever element carries that id first in tree order, so the
    // modal's label would drive the input behind it instead of its own. `id` stays
    // the logical name the form knows the field by, which is what `setValue` needs.
    const inputId = useId();
    const [isFocused, setIsFocused] = useState(false);
    const showClearButton = isFocused && inputValue !== undefined && inputValue !== '' && !disabled && !error;

    const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      if (onFocusFromProps) {
        onFocusFromProps(event);
      }
    };

    const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      if (onBlurFromProps) {
        onBlurFromProps(event);
      }
    };

    const handleOnClear = () => {
      if (setValue) {
        setValue(id, '');
        setIsFocused(true);
      }
    };

    if (floatStyle) {
      return (
        <div
          className={classnames(
            leftPadding && 'pl-lg',
            rightPadding && 'pr-lg',
            'flex w-full flex-col',
            disabled && 'opacity-40',
          )}>
          <div
            className={classnames(
              'px-md gap-xs py-xs relative flex items-center rounded-sm border',
              error ? 'border-border_negative' : 'border-border_regular',
              'float-group',
            )}>
            {leftIcon && <div className="w-7">{leftIcon}</div>}
            <div className="relative w-full">
              {label && (
                <label
                  htmlFor={inputId}
                  className={classnames(
                    'text-fg_secondary text-l absolute left-0 top-2 block',
                    disabled && 'text-fg_disabled',
                  )}>
                  {label}
                </label>
              )}
              <input
                id={inputId}
                ref={ref}
                type={type}
                className={twMerge(
                  'text-fg_primary text-l mt-xs peer w-full placeholder-transparent outline-none disabled:bg-transparent',
                  className,
                )}
                disabled={disabled}
                placeholder={label}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                {...props}
              />
            </div>
            {showClearButton && (
              <Icon
                className="clear-button cursor-pointer"
                variant="close-circle"
                color="fg_secondary"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleOnClear}
              />
            )}
            {rightIcon}
          </div>
          {error && <div className="text-fg_negative text-s mt-xxs flex justify-start">{error}</div>}
        </div>
      );
    }

    return (
      <div
        className={classnames(
          leftPadding && 'pl-lg',
          rightPadding && 'pr-lg',
          'flex w-full flex-col',
          disabled && 'opacity-40',
        )}>
        {label && (
          <label htmlFor={inputId} className="text-fg_primary pt-md text-m block">
            {label}
          </label>
        )}
        <div
          className={classnames(
            'pr-lg relative flex border-b',
            error ? 'border-border_negative' : 'border-border_regular',
          )}>
          {leftIcon && <div className="pr-md mt-md">{leftIcon}</div>}

          <input
            id={inputId}
            ref={ref}
            type={type}
            className={twMerge(
              'placeholder:text-fg_secondary text-fg_primary py-md w-full text-sm outline-none disabled:bg-transparent',
              className,
            )}
            disabled={disabled}
            {...props}
          />
          {error && !rightIcon && (
            <div className="pr-md mt-md">
              <Icon variant="warning" color="fg_negative" />
            </div>
          )}
          {rightIcon && <div className="pr-md mt-md">{rightIcon}</div>}
        </div>
        {error && <div className="text-fg_negative text-xxs mt-xxs flex justify-start">{error}</div>}
      </div>
    );
  },
);

export default ListInput;
